import type { LandscapeOutputData, KeywordRanking } from "@/types/landscape";
import {
  formatKeywordPendingMoney,
  formatKeywordPendingNumber,
  formatKeywordPendingRank,
} from "@/utils/keywordPendingDisplay";

function escapeCsvCell(val: unknown): string {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Same CSV shape single-landscape export has always used -- shared so bulk export can't drift from it. */
export function buildLandscapeCsv(data: LandscapeOutputData, keywords?: KeywordRanking[]): string {
  const rows = keywords ?? data.keywords;
  const heroIndex = data.products.findIndex((p) => p.isHero);

  const headers = [
    "Keyword",
    "Hero Rank",
    "Competitors",
    "Search Volume",
    "KPS",
    "Relevance",
    "Trend %",
    "PPC Max Bid",
  ];

  const parts: string[] = [headers.join(",") + "\n"];

  for (const kw of rows) {
    const row = [
      escapeCsvCell(kw.keyword),
      escapeCsvCell(formatKeywordPendingRank(kw.ranks[heroIndex] ?? null)),
      escapeCsvCell(kw.competitors),
      escapeCsvCell(kw.exactVol),
      escapeCsvCell(formatKeywordPendingNumber(kw.kpg)),
      escapeCsvCell(formatKeywordPendingNumber(kw.hero_relevance)),
      escapeCsvCell(`${kw.search_volume}%`),
      escapeCsvCell(formatKeywordPendingMoney(kw.ppc_max_bid)),
    ].join(",");
    parts.push(row + "\n");
  }

  return parts.join("");
}

/**
 * Filesystem/zip-safe slug: collapses any run of characters outside
 * [a-z0-9] (including "/", which would otherwise create a nested folder
 * inside a zip -- see landscapeCsvFilename's callers) into a single "-",
 * then trims leading/trailing dashes.
 */
function slugify(phrase: string): string {
  const slug = phrase
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "landscape";
}

export function landscapeCsvFilename(data: LandscapeOutputData): string {
  const slug = slugify(data.prioritySearchPhrase);
  const date = new Date().toISOString().slice(0, 10);
  return `landscape-${slug}-${date}.csv`;
}

/** Shared by the single- and bulk-export flows so the download boilerplate lives in one place. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
